import { useState } from 'react';
import {
  Fab,
  Tooltip,
  useTheme,
  useMediaQuery,
  Box,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { useAiAssistantEligibility } from '../hooks/useAiAssistantEligibility';
import { AiAssistantDrawer } from './AiAssistantDrawer';
import { shouldShowMobileBottomNav, BOTTOM_NAV_HEIGHT } from '@/mobile/layouts/MobileShell';
import { vyora } from '@/themes/tokens';

export function AiAssistantFab() {
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down('sm'));
  const isSm = useMediaQuery(theme.breakpoints.down('md'));
  const showBottomNav = shouldShowMobileBottomNav(isXs);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const { isAvailable } = useAiAssistantEligibility();

  // If user or society does not have AI Assistant entitlement/permission, do not render
  if (!isAvailable) {
    return null;
  }

  // Calculate bottom offset respecting mobile bottom navigation and safe-area insets
  const bottomOffset = showBottomNav
    ? `calc(${BOTTOM_NAV_HEIGHT}px + 16px + env(safe-area-inset-bottom, 0px))`
    : { xs: 'calc(16px + env(safe-area-inset-bottom, 0px))', sm: '24px' };

  return (
    <>
      <Box
        className="no-print"
        sx={{
          position: 'fixed',
          bottom: bottomOffset,
          right: { xs: 16, sm: 24 },
          zIndex: (t) => t.zIndex.speedDial,
        }}
      >
        <Tooltip title={isSm ? '' : 'AI Assistant'} placement="left" arrow>
          <Fab
            color="primary"
            aria-label="Open AI Assistant"
            onClick={() => setDrawerOpen((prev) => !prev)}
            sx={{
              width: { xs: 48, sm: 54 },
              height: { xs: 48, sm: 54 },
              minHeight: 48,
              background: `linear-gradient(135deg, ${vyora.teal} 0%, ${vyora.navy} 100%)`,
              color: '#FFFFFF',
              boxShadow:
                theme.palette.mode === 'dark'
                  ? '0 6px 20px rgba(0, 184, 148, 0.4)'
                  : '0 6px 18px rgba(0, 184, 148, 0.35)',
              transition: 'transform 0.2s cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 0.2s ease',
              '&:hover': {
                transform: 'scale(1.08)',
                background: `linear-gradient(135deg, ${vyora.aqua} 0%, ${vyora.navy} 100%)`,
                boxShadow: '0 8px 24px rgba(0, 184, 148, 0.5)',
              },
              '&:focus-visible': {
                outline: `3px solid ${vyora.aqua}`,
                outlineOffset: 2,
              },
            }}
          >
            <AutoAwesomeIcon sx={{ fontSize: { xs: 22, sm: 24 } }} />
          </Fab>
        </Tooltip>
      </Box>

      {/* Slide-out AI Assistant Drawer */}
      <AiAssistantDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} />
    </>
  );
}
